
// Módulo de armazenamento simples no lado do cliente
// usando o IndexedDB para armazenar e recuperar dados.

const DB_NAME = 'aivax-storage';
const DB_VERSION = 1;

let dbPromise = null;
const collectionInstances = new Map();

function openDatabase(storeName) {
    if (dbPromise) {
        return dbPromise.then(db => {
            if (!db.objectStoreNames.contains(storeName)) {
                db.close();
                dbPromise = null;
                return openDatabase(storeName);
            }
            return db;
        });
    }

    dbPromise = new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME);

        request.onerror = () => reject(request.error);

        request.onsuccess = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains(storeName)) {
                const newVersion = db.version + 1;
                db.close();

                const upgradeRequest = indexedDB.open(DB_NAME, newVersion);
                upgradeRequest.onerror = () => reject(upgradeRequest.error);
                upgradeRequest.onsuccess = () => resolve(upgradeRequest.result);
                upgradeRequest.onupgradeneeded = (event) => {
                    const upgradedDb = event.target.result;
                    if (!upgradedDb.objectStoreNames.contains(storeName)) {
                        upgradedDb.createObjectStore(storeName, { keyPath: 'key' });
                    }
                };
            } else {
                resolve(db);
            }
        };

        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            if (!db.objectStoreNames.contains(storeName)) {
                db.createObjectStore(storeName, { keyPath: 'key' });
            }
        };
    });

    return dbPromise;
}

/**
 * Classe que representa uma coleção de armazenamento.
 */
class StorageCollection {
    /**
     * @param {string} collectionName - Nome da coleção/store.
     */
    constructor(collectionName) {
        this.collectionName = collectionName;
    }

    /**
     * Recupera um valor do armazenamento pela sua chave.
     * @param {string} key - A chave do item a ser recuperado.
     * @returns {Promise<any>} - O valor armazenado ou undefined se não existir.
     */
    async get(key) {
        const db = await openDatabase(this.collectionName);
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(this.collectionName, 'readonly');
            const store = transaction.objectStore(this.collectionName);
            const request = store.get(key);

            request.onerror = () => reject(request.error);
            request.onsuccess = () => {
                const result = request.result;
                resolve(result ? result.value : undefined);
            };
        });
    }

    /**
     * Armazena um valor com a chave especificada.
     * @param {string} key - A chave do item.
     * @param {any} value - O valor a ser armazenado.
     * @returns {Promise<void>}
     */
    async set(key, value) {
        const db = await openDatabase(this.collectionName);
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(this.collectionName, 'readwrite');
            const store = transaction.objectStore(this.collectionName);
            const request = store.put({ key, value });

            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve();
        });
    }

    /**
     * Lista todos os itens armazenados na coleção.
     * @returns {Promise<Array<{ key: string, value: any }>>}
     */
    async list() {
        const db = await openDatabase(this.collectionName);
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(this.collectionName, 'readonly');
            const store = transaction.objectStore(this.collectionName);
            const request = store.getAll();

            request.onerror = () => reject(request.error);
            request.onsuccess = () => {
                const results = request.result || [];
                resolve(results.map(item => ({ key: item.key, value: item.value })));
            };
        });
    }

    /**
     * Remove um item do armazenamento pela chave.
     * @param {string} key - A chave do item a ser removido.
     * @returns {Promise<void>}
     */
    async remove(key) {
        const db = await openDatabase(this.collectionName);
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(this.collectionName, 'readwrite');
            const store = transaction.objectStore(this.collectionName);
            const request = store.delete(key);

            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve();
        });
    }

    /**
     * Limpa todos os itens da coleção.
     * @returns {Promise<void>}
     */
    async clear() {
        const db = await openDatabase(this.collectionName);
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(this.collectionName, 'readwrite');
            const store = transaction.objectStore(this.collectionName);
            const request = store.clear();

            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve();
        });
    }
}

/**
 * Abre uma coleção de armazenamento pelo nome.
 * @param {string} collectionName - Nome da coleção.
 * @returns {Promise<StorageCollection>} - Instância da coleção.
 * @example
 * const notes = await openStorage("notes");
 * await notes.set("note1", { title: "Minha nota", content: "..." });
 * const note = await notes.get("note1");
 * const allNotes = await notes.list();
 * await notes.remove("note1");
 * await notes.clear();
 */
export async function openStorage(collectionName) {
    if (collectionInstances.has(collectionName)) {
        return collectionInstances.get(collectionName);
    }

    const collection = new StorageCollection(collectionName);
    await openDatabase(collectionName);
    collectionInstances.set(collectionName, collection);

    return collection;
}